<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaTracking,
  gaiaFonts,
  gaiaFontWeight,
  gaiaHeadingSize,
  gaiaNeutral,
  gaiaStatus,
} from '@/components/design/themes/tokens';
import { useRouter } from 'vue-router';
import { NButton, NIcon, NSkeleton } from 'naive-ui';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import TemplateCreateModal from '@/components/contractGeneration/templates/TemplateCreateModal.vue';
import DirectoryPickerModal from './DirectoryPickerModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import {
  IconAdd,
  IconCheckCircleFilled,
  IconDocumentText,
  IconChevronDown,
  IconChevronForward,
  IconEdit,
  IconFolder,
} from '@/components/design/icons';
import {
  TaskStatus,
  type ContractGenerationTemplateDraftability,
  type ContractGenerationTemplateInDb,
  type ContractGenerationTemplateQuestionInDb,
  type ContractGenerationTemplateVariantInDb,
  type ContractGenerationTemplateVariantSummary,
  type ContractGenerationVariantAnswersSummary,
} from '@/api';
import { TemplateQuestionsService } from '@/services/contractGeneration/templateQuestionsService';
import { TemplateVariantsService } from '@/services/contractGeneration/templateVariantsService';
import { draftBlockedMessage, toMatchAnswers } from '@/utils/contractGeneration/questions';
import { logger } from '@/utils/logger';
import QuestionAnswerFields from '@/components/contractGeneration/questions/QuestionAnswerFields.vue';
import VariantCandidateList from '@/components/contractGeneration/questions/VariantCandidateList.vue';
import { useTemplateValidationPolling } from '@/composables/contractGeneration/useTemplateValidationPolling';
import { useContractGenerationStore } from '@/stores/contractGenerationStore';
import type { TemplateSelectionState } from '@/types/contractGeneration/wizard';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';

/**
 * The wizard's first step, in two screens: which template, then which version
 * of it — plus the folder the document is created in, asked up front: the
 * draft is filed there from its first save and stays put (it moves from the
 * vault, like any contract).
 *
 * The second screen is only ever *seen* when there is something to decide.
 * Picking a template whose single version is ready selects it and the wizard
 * moves on, so the screen that would have asked nothing never appears. That
 * also means the awkward states — no versions at all, or a lone version that
 * failed validation — land there and can explain themselves, rather than being
 * special-cased somewhere the person cannot act on them.
 *
 * The customer-facing word is **version**. "Variant" is the product's word for
 * the same thing and is what the template pages say; nobody drafting a document
 * should have to learn it.
 */
interface Props {
  selection: TemplateSelectionState;
  /**
   * Which screen to show. Owned by the wizard rather than worked out here, so
   * the step rail and the pane can never disagree about where the person is.
   */
  stage: 'template' | 'version';
  disabled?: boolean;
  /**
   * The draft already exists, so its template is fixed: the picker collapses to
   * the chosen template and only the variant can still change. Switching to a
   * different template would orphan every value entered so far — that is what
   * "New document" is for.
   */
  lockedTemplate?: boolean;
  /** Where the document is created, root first: ["My Vault", "Agreements"]. */
  folderPath: string[];
  /** The document's name as typed; empty means the default, named after the template. */
  fileName: string;
}

/** A folder from the picker. */
export interface FolderChoice {
  uid: string;
  label: string;
  breadcrumb_labels: string[];
}

const props = defineProps<Props>();
const folderPickerOpen = ref(false);
/** What an empty name becomes once the template is picked. */
const defaultNameExample = `Template name - ${new Date().toISOString().slice(0, 10)}`;
const shownPath = computed(() => (props.folderPath.length > 0 ? props.folderPath : ['My Vault']));
const emit = defineEmits<{
  (e: 'update:selection', value: TemplateSelectionState): void;
  /** Back to the template list — the wizard owns the step, so it does the move. */
  (e: 'back'): void;
  /**
   * The chosen template left something to decide. Said explicitly rather than
   * inferred from the version list, which is empty both when none exist and
   * while they are still loading.
   */
  (e: 'needs-version'): void;
  (e: 'update:folder', value: FolderChoice): void;
  (e: 'update:fileName', value: string): void;
}>();

const router = useRouter();
const store = useContractGenerationStore();

const searchQuery = ref<string>('');
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);

const variantsLoading = ref<boolean>(false);
const variantLoading = ref<boolean>(false);
const variantError = ref<string | null>(null);

const selectedTemplateUid = computed(() => props.selection.template_uid);

async function fetchTemplates(): Promise<void> {
  try {
    await store.fetchTemplates({
      text: searchQuery.value.trim(),
      page: currentPage.value,
      pageSize: pageSize.value,
    });
  } catch {
    // Surfaced via store.error
  }
}

// Page 1 is reached either by fetching directly or by letting the page watcher
// fire — never both. The field owns the debounce.
function applySearch(): void {
  if (currentPage.value === 1) {
    void fetchTemplates();
  } else {
    currentPage.value = 1;
  }
}

function handlePageChange(page: number): void {
  currentPage.value = page;
}

function handlePageSizeChange(size: number): void {
  pageSize.value = size;
  currentPage.value = 1;
}

watch(currentPage, () => {
  void fetchTemplates();
});

watch(pageSize, () => {
  void fetchTemplates();
});

async function selectTemplate(template: ContractGenerationTemplateInDb): Promise<void> {
  variantError.value = null;
  variantsLoading.value = true;
  // Clear the previous template's variants while the new ones load — otherwise
  // an out-of-date "failed" badge could linger in the picker.
  emit('update:selection', {
    template_uid: template.uid,
    template_name: template.name,
    variant_name: null,
    template_variant_uid: null,
    extracted_schema: null,
    field_metadata: null,
    available_variants: [],
    template_status: null,
  });

  try {
    const variants = await TemplateVariantsService.list(template.uid);
    if (variants.length === 0) {
      // Nothing to draft from — which the version screen is the place to say.
      emit('needs-version');
      return;
    }

    emit('update:selection', {
      template_uid: template.uid,
      template_name: template.name,
      variant_name: null,
      template_variant_uid: null,
      extracted_schema: null,
      field_metadata: null,
      available_variants: variants,
      template_status: null,
    });

    // A lone ready version is nothing to decide, so it is taken and the wizard
    // moves on — the second screen, having nothing to ask, is never seen. One
    // that is broken or still validating is *not* taken: that screen then shows
    // why, which is the only place the person can do anything about it.
    const onlyVariant = variants.length === 1 ? variants[0] : null;
    if (onlyVariant && onlyVariant.template_status === TaskStatus.DONE) {
      await selectVariant(template, onlyVariant.uid, variants);
    } else {
      emit('needs-version');
    }
  } catch (error) {
    variantError.value = error instanceof Error ? error.message : 'Failed to load variants.';
  } finally {
    variantsLoading.value = false;
  }
}

async function selectVariant(
  template: { uid: string; name: string },
  variantUid: string,
  /**
   * The list to fold the read row into. `selectTemplate` passes the variants
   * it just fetched rather than letting this read `props.selection`: the emit
   * carrying them has not reached us as a prop yet when it auto-picks, and
   * mapping over the stale (empty) list would emit the list away again.
   */
  knownVariants: ContractGenerationTemplateVariantSummary[] = props.selection.available_variants
): Promise<void> {
  variantLoading.value = true;
  variantError.value = null;

  try {
    const detail: ContractGenerationTemplateVariantInDb = await TemplateVariantsService.get(
      template.uid,
      variantUid
    );

    // Fold the freshly-read row back into the list behind the picker, so a
    // variant that flipped to `done` while polling becomes selectable at once
    // — and a rename made elsewhere shows up — without a manual reload.
    const refreshed = knownVariants.map(variant =>
      variant.uid === detail.uid
        ? {
            ...variant,
            name: detail.name,
            template_status: detail.template_status ?? null,
            file_name: detail.file_name,
            mime_type: detail.mime_type,
            vault_file_uid: detail.vault_file_uid ?? null,
            vault_file_revision_uid: detail.vault_file_revision_uid ?? null,
            updated_at: detail.updated_at ?? null,
          }
        : variant
    );

    emit('update:selection', {
      template_uid: template.uid,
      template_name: template.name,
      variant_name: detail.name,
      template_variant_uid: detail.uid,
      extracted_schema: detail.extracted_schema ?? null,
      field_metadata: detail.field_metadata ?? null,
      available_variants: refreshed,
      template_status: detail.template_status ?? null,
    });

    // Surface validation status so the user knows why Continue might be off.
    if (detail.template_status === TaskStatus.FAILED) {
      const messages =
        (detail.validation_errors ?? [])
          .map(v => v.message)
          .filter(Boolean)
          .join('; ') || 'Template validation failed.';
      variantError.value = `Template validation failed: ${messages}`;
    } else if (
      detail.template_status === TaskStatus.RUNNING ||
      detail.template_status === TaskStatus.PENDING
    ) {
      variantError.value =
        'This template is still being validated. Continue will unlock once validation completes.';
    } else if (!detail.extracted_schema) {
      variantError.value =
        'This template file has no extracted schema. Re-upload the template to extract fields.';
    }
  } catch (error) {
    variantError.value = error instanceof Error ? error.message : 'Failed to load template schema.';
  } finally {
    variantLoading.value = false;
  }
}

// ──────────────────────────────────────────────────────────
// Auto-refresh while the picked variant is still being validated
// ──────────────────────────────────────────────────────────

const isCurrentVariantValidating = computed<boolean>(
  () =>
    props.selection.template_status === TaskStatus.PENDING ||
    props.selection.template_status === TaskStatus.RUNNING
);

async function refreshSelectedVariant(): Promise<void> {
  if (!props.selection.template_uid || !props.selection.template_variant_uid) return;
  await selectVariant(
    {
      uid: props.selection.template_uid,
      name: props.selection.template_name ?? '',
    },
    props.selection.template_variant_uid
  );
}

useTemplateValidationPolling(isCurrentVariantValidating, refreshSelectedVariant);

const hasActiveSearch = computed(() => searchQuery.value.trim().length > 0);

// ──────────────────────────────────────────────────────────
// A template that isn't there yet
// ──────────────────────────────────────────────────────────
//
// Someone about to draft who finds their template missing can upload it here
// instead of leaving for the Templates page. Anyone who can draft can create
// templates (the backend gates both on the contract-generation feature alone),
// so the action needs no permission check of its own.
//
// The new template is picked at once. Its version is still validating, so the
// version screen shows it as such, polls, and — like the opening pick of any
// lone ready version — carries straight on into the document when it is done.

const createTemplateOpen = ref<boolean>(false);

function handleTemplateCreated(template: ContractGenerationTemplateInDb): void {
  store.upsertTemplate(template);
  void selectTemplate(template);
}

const isValidating = (status: TaskStatus | null | undefined): boolean =>
  status === TaskStatus.PENDING || status === TaskStatus.RUNNING;

const anyVersionValidating = computed<boolean>(
  () =>
    props.stage === 'version' &&
    props.selection.available_variants.some(variant => isValidating(variant.template_status))
);

async function refreshValidatingVersions(): Promise<void> {
  const template = { uid: props.selection.template_uid, name: props.selection.template_name };
  if (!template.uid) return;
  let variants: ContractGenerationTemplateVariantSummary[];
  try {
    variants = await TemplateVariantsService.list(template.uid);
  } catch {
    return; // The next tick tries again.
  }
  if (props.selection.template_uid !== template.uid) return;
  emit('update:selection', { ...props.selection, available_variants: variants });
  const onlyVariant = variants.length === 1 ? variants[0] : null;
  if (
    onlyVariant?.template_status === TaskStatus.DONE &&
    !props.selection.template_variant_uid &&
    !asksQuestions.value
  ) {
    await selectVariant(
      { uid: template.uid, name: template.name ?? '' },
      onlyVariant.uid,
      variants
    );
  }
}

useTemplateValidationPolling(anyVersionValidating, refreshValidatingVersions);

// ──────────────────────────────
// Questions — answering them narrows which document is used.
// A template that asks none just lists its versions to pick from.
// ──────────────────────────────

const questions = ref<ContractGenerationTemplateQuestionInDb[]>([]);
/** The template's own verdict: whether answers can resolve a version at all. */
const draftability = ref<ContractGenerationTemplateDraftability | null>(null);
/** question uid → the typed value. Ephemeral: the draft stores the variant. */
const questionAnswers = ref<Record<string, string>>({});
const candidates = ref<ContractGenerationVariantAnswersSummary[]>([]);
const questionsLoading = ref<boolean>(false);
const matching = ref<boolean>(false);
const matchError = ref<string | null>(null);

/** Bumped per template, so a late response from the previous one is dropped. */
let questionsToken = 0;
/** Bumped per match, so a slow early answer can't overwrite a newer one. */
let matchSeq = 0;

const asksQuestions = computed<boolean>(() => questions.value.length > 0);

/**
 * Why this template cannot be drafted from, in the words of someone who can do
 * nothing about it. Answering is pointless while this is set — the backend
 * refuses to match at all — so the form gives way to the explanation.
 */
const blockedReason = computed<string | null>(() => draftBlockedMessage(draftability.value));

/**
 * The versions to choose from, however the person got here: what the answers
 * left standing, or simply all of them when the template asks nothing. One
 * shape so one picker renders both — the list means the same thing either way.
 */
const pickableVersions = computed<ContractGenerationVariantAnswersSummary[]>(() => {
  if (asksQuestions.value) return candidates.value;
  return props.selection.available_variants.map(variant => ({
    variant_uid: variant.uid,
    variant_name: variant.name,
    template_status: variant.template_status ?? null,
    answers: [],
    unanswered_required_question_uids: [],
  }));
});

/**
 * Where the person stands, in one line. Deliberately different when the
 * template asks questions: there, the count is something they can still act on
 * by answering more, and saying so is what stops a list of five reading as a
 * dead end.
 */
const pickerHint = computed<string>(() => {
  if (matching.value) return 'Checking which versions fit…';
  const count = pickableVersions.value.length;
  if (count === 1 && anyVersionValidating.value) {
    return 'Checking the template file — this takes a moment. You carry straight on when it is ready.';
  }
  if (!asksQuestions.value) {
    return count === 1 ? 'Select it to carry on.' : 'Select the one you need to carry on.';
  }
  return count === 1
    ? 'Your answers leave one. Select it to carry on.'
    : 'Answer more above to narrow these down, or select one now.';
});

/** Back to the list, forgetting everything the chosen template brought with it. */
function backToTemplates(): void {
  emit('back');
  // Bump the token so a questions or match response still in flight for the
  // template being left cannot land on the empty screen.
  questionsToken += 1;
  questions.value = [];
  draftability.value = null;
  questionAnswers.value = {};
  candidates.value = [];
  matchError.value = null;
  variantError.value = null;
  emit('update:selection', {
    template_uid: null,
    template_name: null,
    variant_name: null,
    template_variant_uid: null,
    extracted_schema: null,
    field_metadata: null,
    available_variants: [],
    template_status: null,
  });
}

/**
 * The version already chosen no longer fits what is answered now — reachable by
 * going back a step and changing an answer. Said plainly, because the card for
 * it is simply absent from the list and its selection is otherwise invisible.
 */
const pickedNoLongerFits = computed<boolean>(() => {
  const picked = props.selection.template_variant_uid;
  if (!picked || !asksQuestions.value || matching.value) return false;
  if (candidates.value.length === 0) return false;
  return !candidates.value.some(candidate => candidate.variant_uid === picked);
});

/**
 * Every document consistent with what has been answered so far.
 *
 * Matching narrows, it does not resolve: with nothing answered every document
 * comes back, and each answer removes the ones that don't cover it.
 *
 * **A single match does not move the wizard on, except from the opening one.**
 * Picking a template whose only version needs no narrowing is the one case
 * where continuing silently cannot be wrong — nothing has been typed for it to
 * contradict. After that, a lone match is very often a half-typed number: `1`
 * on the way to `1200` matches a band the finished figure doesn't, and
 * `1200` may match nothing at all. Continuing on it carries the person out of
 * the step mid-sentence and into a document chosen by a prefix, so from the
 * first answer onwards the list is shown and the pick is theirs.
 */
async function runMatch(
  templateUid: string,
  { token = questionsToken, fromAnswers = true }: { token?: number; fromAnswers?: boolean } = {}
): Promise<void> {
  const seq = ++matchSeq;
  matching.value = true;
  matchError.value = null;
  try {
    const response = await TemplateQuestionsService.match(
      templateUid,
      toMatchAnswers(questions.value, questionAnswers.value)
    );
    // Answers change faster than requests come back: only the newest one, for
    // the template still being looked at, may touch the list.
    if (token !== questionsToken || seq !== matchSeq) return;
    const matched = response.variants ?? [];
    candidates.value = matched;

    if (fromAnswers) return;
    const only = matched.length === 1 ? matched[0] : null;
    const usable =
      only && (only.template_status === null || only.template_status === TaskStatus.DONE);
    // Never *change* a choice, only make one where none exists. A restored
    // draft arrives naming its variant, and a template whose other versions
    // have since been deleted would otherwise have its document swapped.
    if (only && usable && props.selection.template_variant_uid === null) {
      await selectVariant(
        { uid: templateUid, name: props.selection.template_name ?? '' },
        only.variant_uid
      );
    }
  } catch (error) {
    if (token !== questionsToken || seq !== matchSeq) return;
    // A 409 here is the template saying it isn't ready to draft from at all —
    // its message names what its owner has to fix.
    matchError.value = error instanceof Error ? error.message : 'Failed to match the answers.';
  } finally {
    if (token === questionsToken && seq === matchSeq) matching.value = false;
  }
}

/**
 * Typing shouldn't fire a request per keystroke; the answers settle first.
 * Long enough to sit through a number being typed out, since every
 * intermediate value is a legitimate answer the backend would happily match.
 */
const debouncedMatch = useDebounceFn(() => {
  const templateUid = props.selection.template_uid;
  if (templateUid && asksQuestions.value) void runMatch(templateUid);
}, 500);

function handleAnswers(next: Record<string, string>): void {
  questionAnswers.value = next;
  void debouncedMatch();
}

async function loadQuestions(templateUid: string): Promise<void> {
  const token = ++questionsToken;
  questions.value = [];
  draftability.value = null;
  questionAnswers.value = {};
  candidates.value = [];
  matchError.value = null;
  questionsLoading.value = true;
  try {
    const state = await TemplateQuestionsService.list(templateUid);
    if (token !== questionsToken) return;
    questions.value = state.questions ?? [];
    draftability.value = state.draftability;
    // Matching a template that isn't ready is a guaranteed 409, and its message
    // is written for whoever owns the template. Ask the question we already know
    // the answer to, and say it in the drafting person's words instead.
    if (blockedReason.value !== null) return;
    // The opening match, with nothing answered — the only one that may pick.
    if (questions.value.length > 0) await runMatch(templateUid, { token, fromAnswers: false });
  } catch (error) {
    if (token !== questionsToken) return;
    // Degrade rather than block: a template whose questions can't be read is
    // still draftable by picking a version from the list.
    logger.warn('Failed to load the template’s questions:', error);
  } finally {
    if (token === questionsToken) questionsLoading.value = false;
  }
}

function goToTemplatesView(): void {
  void router.push({ name: 'contract-generation-templates' });
}

/**
 * Pull the version list for the picked template so the picker can show each
 * one's status (and disable broken / still-validating ones). Used on mount
 * when we land here via a URL-preselected template or a draft restore —
 * `selectTemplate` already does the fetch on direct picks.
 */
async function fetchVariants(templateUid: string): Promise<void> {
  try {
    const variants = await TemplateVariantsService.list(templateUid);
    // Guard against a template switch landing the response on the wrong list.
    if (props.selection.template_uid !== templateUid) return;
    emit('update:selection', { ...props.selection, available_variants: variants });
  } catch {
    // Non-fatal: the picker just shows whatever the parent already knows.
  }
}

onMounted(() => {
  // A locked pane never lists templates — there is nothing to pick from.
  if (!props.lockedTemplate) void fetchTemplates();
  if (props.selection.template_uid) {
    void loadQuestions(props.selection.template_uid);
    if (props.selection.available_variants.length === 0) {
      void fetchVariants(props.selection.template_uid);
    }
  }
});

// When the parent (wizard) swaps template via init/draft restore, refresh the
// list so the picker reflects the new template's versions.
watch(
  () => props.selection.template_uid,
  uid => {
    if (!uid) return;
    void loadQuestions(uid);
    if (props.selection.available_variants.length === 0) {
      void fetchVariants(uid);
    }
  }
);

// --- chrome (tokens for the scoped styles) ---
const fsBody = gaiaFontSize.body;
const fwSemibold = String(gaiaFontWeight.semibold);
const fwBold = String(gaiaFontWeight.bold);
const trackDisplay = gaiaTracking.display;
const trackCaps = gaiaTracking.labelSoft;
const accent = gaiaAccent.base;
const accentWash = gaiaAccent.tint;
const fontMono = gaiaFonts.mono;
const fsHeadDialog = gaiaHeadingSize.dialog;
const fsMeta = gaiaFontSize.meta;
const fsRow = gaiaFontSize.row;
const fsSubtitle = gaiaFontSize.subtitle;
const gBorder = gaiaNeutral.borderCard;
const gBorderHover = gaiaNeutral.borderHover;
const gBorderStrong = gaiaNeutral.border;
const gInk = gaiaNeutral.ink;
const gMuted = gaiaNeutral.surfaceMuted;
const gSoft = gaiaNeutral.surfaceSoft;
const gSubtle = gaiaNeutral.textSubtle;
const gTint = gaiaNeutral.surfaceTint;
const gSuccess = gaiaStatus.successText;
const gSurface = gaiaNeutral.surface;
const gText = gaiaNeutral.text;
</script>

<template>
  <div class="setup-pane" data-tour="tpl.setup">
    <!-- A new document reads as its own header: the name is the title, edited
         in place (left untitled, it takes the template's), and the folder is a
         chip under it — both settled before the draft exists, then fixed (the
         document moves from the vault after that). -->
    <header v-if="stage === 'template' && !lockedTemplate" class="doc-header">
      <span class="doc-header__eyebrow">
        <n-icon :component="IconDocumentText" :size="14" />
        New document
      </span>
      <label class="doc-header__title-row">
        <input
          class="doc-header__title"
          :value="fileName"
          placeholder="Untitled document"
          aria-label="Document name"
          maxlength="200"
          :disabled="disabled"
          @input="emit('update:fileName', ($event.target as HTMLInputElement).value)"
        />
        <n-icon :component="IconEdit" :size="16" class="doc-header__edit" />
      </label>
      <div class="doc-header__meta">
        <button
          type="button"
          class="folder-chip"
          :disabled="disabled"
          title="Change folder"
          @click="folderPickerOpen = true"
        >
          <n-icon :component="IconFolder" :size="14" class="folder-chip__icon" />
          <template v-for="(segment, position) in shownPath" :key="position">
            <n-icon
              v-if="position > 0"
              :component="IconChevronForward"
              :size="10"
              class="folder-chip__separator"
            />
            <span :class="{ 'folder-chip__current': position === shownPath.length - 1 }">
              {{ segment }}
            </span>
          </template>
          <n-icon :component="IconChevronDown" :size="12" class="folder-chip__caret" />
        </button>
        <span class="doc-header__hint">
          {{
            fileName.trim()
              ? 'Saved as a .docx'
              : `Untitled, it’s named after the template — ${defaultNameExample}`
          }}
        </span>
      </div>
    </header>
    <header v-else class="setup-hero">
      <h2 class="setup-hero__title">
        {{ stage === 'template' ? 'Start from a template' : 'Nearly there' }}
      </h2>
      <p class="setup-hero__subtitle">
        {{
          stage === 'template'
            ? 'Name it, choose where it goes and pick a template — you are straight into the document.'
            : 'One more choice and you can start filling in the document.'
        }}
      </p>
    </header>
    <directory-picker-modal
      v-model:show="folderPickerOpen"
      @select="(choice: FolderChoice) => emit('update:folder', choice)"
    />

    <div class="setup-grid">
      <section class="setup-panel">
        <header class="setup-panel__header">
          <div class="setup-panel__heading">
            <!-- Which template this is narrowing down, and nothing more: the
                 step rail is how you go back, so a second control for it here
                 would only be a third way to do the same thing. -->
            <gaia-text
              v-if="stage === 'version'"
              size="meta"
              tone="subtle"
              class="setup-panel__context"
            >
              {{ selection.template_name }}
            </gaia-text>
            <h3 class="setup-panel__title">
              {{
                stage === 'template'
                  ? 'Choose a template'
                  : asksQuestions
                    ? 'Let’s find the right version'
                    : 'Choose a version'
              }}
            </h3>
            <p class="setup-panel__subtitle">
              {{
                stage === 'template'
                  ? 'Pick one and we’ll take it from there.'
                  : asksQuestions
                    ? 'Answer what you know. Anything you skip just leaves more versions in play.'
                    : 'This template comes in more than one version — pick the one you need.'
              }}
            </p>
          </div>
          <n-flex
            v-if="stage === 'template'"
            align="center"
            :size="12"
            :wrap="false"
            :style="{ marginLeft: 'auto', flexShrink: 0 }"
          >
            <gaia-button text size="small" @click="goToTemplatesView">Manage templates</gaia-button>
            <gaia-create-button
              subject="template"
              variant="default"
              size="sm"
              :disabled="disabled"
              @click="createTemplateOpen = true"
            />
          </n-flex>
        </header>

        <div v-if="stage === 'template'" class="setup-panel__body">
          <gaia-search-input
            v-model:value="searchQuery"
            variant="panel"
            size="medium"
            placeholder="Search templates by name"
            class="template-search"
            @search="applySearch"
          />

          <gaia-banner v-if="store.error" type="error" class="setup-alert">
            {{ store.error }}
          </gaia-banner>

          <div class="template-list" :aria-busy="store.loading">
            <template v-if="store.loading">
              <div
                v-for="i in 4"
                :key="`tpl-skel-${i}`"
                class="template-row template-row--skeleton"
              >
                <NSkeleton circle :height="36" :width="36" />
                <div class="template-row__body">
                  <NSkeleton text :width="180" :height="14" />
                  <NSkeleton text :width="240" :height="12" style="margin-top: 6px" />
                </div>
              </div>
            </template>

            <gaia-empty-state
              v-else-if="store.templates.length === 0 && hasActiveSearch"
              title="No templates match your search"
              class="template-empty"
            >
              Not uploaded yet? Add it now and carry on drafting from it.
              <template #actions>
                <gaia-create-button
                  subject="template"
                  variant="default"
                  size="sm"
                  :disabled="disabled"
                  @click="createTemplateOpen = true"
                />
              </template>
            </gaia-empty-state>

            <div v-else-if="store.templates.length === 0" class="template-empty-state">
              <div class="template-empty-state__icon">
                <NIcon :size="28"><IconDocumentText /></NIcon>
              </div>
              <h4 class="template-empty-state__title">No templates yet</h4>
              <p class="template-empty-state__copy">
                Upload a <code>.docx</code> template with placeholders to start generating
                documents.
              </p>
              <NButton type="primary" size="medium" @click="createTemplateOpen = true">
                <template #icon>
                  <NIcon><IconAdd /></NIcon>
                </template>
                Create template
              </NButton>
            </div>

            <button
              v-for="template in store.templates"
              v-else
              :key="template.uid"
              type="button"
              class="template-row"
              :class="{ 'template-row--active': template.uid === selectedTemplateUid }"
              :disabled="disabled"
              @click="selectTemplate(template)"
            >
              <span class="template-row__icon">
                <NIcon :size="18"><IconDocumentText /></NIcon>
              </span>
              <span class="template-row__body">
                <span class="template-row__title">{{ template.name }}</span>
                <span v-if="template.description" class="template-row__desc">
                  {{ template.description }}
                </span>
              </span>
              <NIcon
                v-if="template.uid === selectedTemplateUid"
                class="template-row__check"
                :size="20"
              >
                <IconCheckCircleFilled />
              </NIcon>
            </button>
          </div>

          <gaia-pagination
            v-if="store.totalCount > 0 && !store.loading"
            :page="currentPage"
            :page-size="pageSize"
            :total="store.totalCount"
            :page-sizes="[5, 10, 25, 50]"
            @update:page="handlePageChange"
            @update:page-size="handlePageSizeChange"
          />
        </div>

        <div v-else class="setup-panel__body variant-block">
          <gaia-banner v-if="variantError" type="warning" class="setup-alert">
            {{ variantError }}
          </gaia-banner>

          <NSkeleton
            v-else-if="variantsLoading || questionsLoading"
            :height="34"
            :width="220"
            style="border-radius: 8px"
          />

          <!-- Not ready, and not by anything the person here can change. The
               form gives way entirely: answering cannot produce a version while
               the template's own versions are incomplete, and leaving the
               inputs up would invite them to try. -->
          <gaia-empty-state
            v-else-if="blockedReason"
            status="alert"
            title="This template isn’t ready to use yet"
          >
            {{ blockedReason }} Whoever looks after it needs to finish setting it up.
            <template v-if="!lockedTemplate" #actions>
              <gaia-button size="small" @click="backToTemplates">
                Choose another template
              </gaia-button>
            </template>
          </gaia-empty-state>

          <!-- Nothing to choose between and nothing to choose: this screen is
               where that has to be said, because it is the one the person is
               looking at when it turns out to be true. -->
          <gaia-empty-state
            v-else-if="selection.available_variants.length === 0"
            title="This template has no versions yet"
          >
            Nothing can be drafted from it until its owner adds one.
            <template v-if="!lockedTemplate" #actions>
              <gaia-button size="small" @click="backToTemplates">
                Choose another template
              </gaia-button>
            </template>
          </gaia-empty-state>

          <template v-else>
            <question-answer-fields
              v-if="asksQuestions"
              :questions="questions"
              :answers="questionAnswers"
              :disabled="disabled || variantLoading"
              @update:answers="handleAnswers"
            />

            <gaia-banner v-if="matchError" type="warning" class="setup-alert">
              {{ matchError }}
            </gaia-banner>

            <template v-else>
              <gaia-empty-state
                v-if="pickableVersions.length === 0 && !matching"
                title="No version covers those answers"
              >
                Change an answer, or ask whoever owns this template to add a version for this
                combination.
              </gaia-empty-state>

              <!-- The list is the way on, whether the answers narrowed it or
                   the template simply has several versions — so it gets a rule
                   and a heading of its own rather than trailing off the last
                   field as a footnote. -->
              <n-flex v-else vertical :size="10" class="version-picker">
                <n-flex vertical :size="2">
                  <n-flex align="baseline" :size="8">
                    <gaia-text size="control" weight="semibold" tone="ink">
                      Pick a version
                    </gaia-text>
                    <gaia-count-pill>{{ pickableVersions.length }}</gaia-count-pill>
                  </n-flex>
                  <gaia-text size="meta" tone="subtle">{{ pickerHint }}</gaia-text>
                </n-flex>

                <gaia-banner v-if="pickedNoLongerFits" type="warning" class="setup-alert">
                  “{{ selection.variant_name }}” is still the chosen version, but it doesn’t cover
                  these answers. Pick one below to change it.
                </gaia-banner>

                <variant-candidate-list
                  :candidates="pickableVersions"
                  :questions="questions"
                  :selected-uid="selection.template_variant_uid"
                  :disabled="disabled || variantLoading"
                  @select="
                    (variantUid: string) =>
                      selection.template_uid &&
                      selectVariant(
                        { uid: selection.template_uid, name: selection.template_name ?? '' },
                        variantUid
                      )
                  "
                />
              </n-flex>
            </template>

            <div
              v-if="selection.template_variant_uid && selection.extracted_schema"
              class="variant-ready"
            >
              <NIcon :size="14" class="variant-ready__icon">
                <IconCheckCircleFilled />
              </NIcon>
              <span>
                {{ selection.variant_name }} ·
                {{ Object.keys(selection.extracted_schema.fields ?? {}).length }} field{{
                  Object.keys(selection.extracted_schema.fields ?? {}).length === 1 ? '' : 's'
                }}
                detected · ready to fill
              </span>
            </div>
          </template>
        </div>
      </section>
    </div>

    <TemplateCreateModal v-model:show="createTemplateOpen" @created="handleTemplateCreated" />
  </div>
</template>

<style scoped>
/* The column is the page here: heading, card and the step rail above them all
   share one centred width. It used to be a 760px card adrift in an 1180px
   block, which read as left-aligned against everything centred around it. */
.setup-pane {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 760px;
  margin: 0 auto;
  width: 100%;
}

.setup-hero {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.setup-hero__title {
  margin: 0;
  font-size: v-bind(fsHeadDialog);
  font-weight: v-bind(fwBold);
  color: v-bind(gInk);
  letter-spacing: v-bind(trackDisplay);
}

.setup-hero__subtitle {
  margin: 0;
  font-size: v-bind(fsBody);
  color: v-bind(gSubtle);
  line-height: 1.5;
  max-width: 720px;
}

/* A new document's header: the name as an editable title, the folder as a
   chip. Native input and button so the title can carry the heading's type —
   no Naive input renders borderless at that size. */
.doc-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.doc-header__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwSemibold);
  letter-spacing: v-bind(trackCaps);
  text-transform: uppercase;
  color: v-bind(accent);
}

.doc-header__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: -10px;
  padding: 2px 10px;
  border-radius: 10px;
  cursor: text;
  transition: background 150ms ease;
}

.doc-header__title-row:hover {
  background: v-bind(gSoft);
}

.doc-header__title-row:focus-within {
  background: v-bind(gSurface);
  box-shadow: 0 0 0 2px v-bind(accentWash);
}

.doc-header__title {
  flex: 1 1 auto;
  min-width: 0;
  padding: 4px 0;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: v-bind(fsHeadDialog);
  font-weight: v-bind(fwBold);
  letter-spacing: v-bind(trackDisplay);
  color: v-bind(gInk);
}

.doc-header__title::placeholder {
  color: v-bind(gSubtle);
  opacity: 0.6;
}

.doc-header__edit {
  flex-shrink: 0;
  color: v-bind(gSubtle);
  opacity: 0;
  transition: opacity 150ms ease;
}

.doc-header__title-row:hover .doc-header__edit {
  opacity: 1;
}

.doc-header__title-row:focus-within .doc-header__edit {
  opacity: 0;
}

.doc-header__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.doc-header__hint {
  font-size: v-bind(fsMeta);
  color: v-bind(gSubtle);
}

.folder-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 5px 10px 5px 8px;
  border: 1px solid v-bind(gBorder);
  border-radius: 999px;
  background: v-bind(gSurface);
  font: inherit;
  font-size: v-bind(fsRow);
  color: v-bind(gSubtle);
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background 150ms ease;
}

.folder-chip:hover:not(:disabled) {
  border-color: v-bind(accent);
  background: v-bind(accentWash);
}

.folder-chip:focus-visible {
  outline: 2px solid v-bind(gInk);
  outline-offset: 2px;
}

.folder-chip:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.folder-chip__icon {
  flex-shrink: 0;
  color: v-bind(accent);
}

.folder-chip__separator,
.folder-chip__caret {
  flex-shrink: 0;
}

.folder-chip__current {
  font-weight: v-bind(fwSemibold);
  color: v-bind(gInk);
  overflow: hidden;
  text-overflow: ellipsis;
}

/* One panel now — the template list reads best at a comfortable measure. */
.setup-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;
  align-items: stretch;
}

.setup-panel {
  display: flex;
  flex-direction: column;
  background: v-bind(gSurface);
  border: 1px solid v-bind(gBorder);
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  overflow: hidden;
  transition: opacity 200ms ease;
}

.setup-panel--inert {
  opacity: 0.55;
  pointer-events: none;
}

.setup-panel__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid v-bind(gMuted);
  background: v-bind(gSoft);
}

.setup-panel__step {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: v-bind(gInk);
  color: v-bind(gSurface);
  font-size: v-bind(fsRow);
  font-weight: v-bind(fwBold);
}

/* The template being narrowed down, named above the heading. */
.setup-panel__context {
  align-self: flex-start;
  margin-bottom: 2px;
}

.setup-panel__heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.setup-panel__title {
  margin: 0;
  font-size: v-bind(fsSubtitle);
  font-weight: v-bind(fwSemibold);
  color: v-bind(gInk);
}

.setup-panel__subtitle {
  margin: 0;
  font-size: v-bind(fsMeta);
  color: v-bind(gSubtle);
}

.setup-panel__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px 20px;
  flex: 1 1 auto;
  min-width: 0;
}

.setup-alert {
  margin: 0;
}

/* Divides the answering from the choosing. Without it the candidates read as
   more of the form rather than the thing the form was narrowing down. */
.version-picker {
  border-top: 1px solid v-bind(gBorder);
  padding-top: 14px;
  margin-top: 2px;
}

.template-search :deep(.n-input) {
  border-radius: 10px;
}

.template-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 320px;
  overflow-y: auto;
  padding: 2px;
  margin: 0 -2px;
}

.template-empty {
  padding: 24px 0;
}

.template-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 32px 20px;
  background: v-bind(gSoft);
  border: 1px dashed v-bind(gBorderStrong);
  border-radius: 12px;
}

.template-empty-state__icon {
  width: 56px;
  height: 56px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: v-bind(gMuted);
  color: v-bind(gSubtle);
}

.template-empty-state__title {
  margin: 4px 0 0;
  font-size: v-bind(fsSubtitle);
  font-weight: v-bind(fwSemibold);
  color: v-bind(gInk);
}

.template-empty-state__copy {
  margin: 0 0 6px;
  font-size: v-bind(fsRow);
  color: v-bind(gSubtle);
  line-height: 1.5;
  max-width: 320px;
}

.template-empty-state__copy code {
  background: v-bind(gBorder);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: v-bind(fsMeta);
  font-family: v-bind(fontMono);
  color: v-bind(gInk);
}

.template-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid v-bind(gBorder);
  border-radius: 10px;
  background: v-bind(gSurface);
  cursor: pointer;
  text-align: left;
  transition:
    border-color 120ms ease,
    background-color 120ms ease,
    box-shadow 120ms ease,
    transform 120ms ease;
}

.template-row:hover:not(:disabled):not(.template-row--skeleton) {
  border-color: v-bind(gBorderHover);
  background: v-bind(gSoft);
}

.template-row:focus-visible {
  outline: 2px solid v-bind(gInk);
  outline-offset: 2px;
}

.template-row--active {
  border-color: v-bind(gInk);
  background: v-bind(gTint);
}

.template-row--skeleton,
.template-row--static {
  cursor: default;
}

.template-row:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.template-row__icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: v-bind(gMuted);
  color: v-bind(gSubtle);
}

.template-row--active .template-row__icon {
  background: v-bind(gInk);
  color: v-bind(gSurface);
}

.template-row__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.template-row__title {
  font-size: v-bind(fsBody);
  font-weight: v-bind(fwSemibold);
  color: v-bind(gInk);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.template-row__desc {
  font-size: v-bind(fsMeta);
  color: v-bind(gSubtle);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-row__check {
  flex-shrink: 0;
  color: v-bind(gInk);
}

.picker-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.variant-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 20px 20px;
  border-top: 1px dashed v-bind(gBorder);
  background: v-bind(gSoft);
}

.variant-block__label {
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwSemibold);
  color: v-bind(gText);
  text-transform: uppercase;
  letter-spacing: v-bind(trackCaps);
}

.variant-ready {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: v-bind(fsMeta);
  color: v-bind(gSuccess);
}

.variant-ready__icon {
  color: v-bind(gSuccess);
}
</style>
